import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { onAuthStateChanged, onIdTokenChanged, type User } from 'firebase/auth'
import { useQueryClient } from '@tanstack/react-query'
import {
  auth,
  completeRedirectSignIn,
  firebaseSignOut,
  googleProvider,
  portalGoogleProvider,
  signInWithGoogle,
  signInWithGoogleRedirect,
} from '@/lib/firebase'
import { isInternalEmail } from '@/lib/auth-policy'
import { qk } from '@/api/query-keys'
import { DEV_USER } from '@/lib/devAuth'

/**
 * `external` is a signed-in person on a NON-internal email — a client, in other words.
 * It replaced `wrong-domain`, which named a rejection that was never ours to make: whether
 * they have access is the backend's answer, and a client with a portal domain does.
 */
export type AuthStatus = 'loading' | 'signed-out' | 'external' | 'signed-in'

/**
 * `portal: true` drops the `hd` hint from Google's account picker.
 *
 * A portal user signs in with their own company's Google account, so hinting our domain
 * shows them the wrong account or an empty picker. It is only ever a hint — the real gate is
 * the backend matching their domain against `clients.allowedDomains`.
 */
export interface SignInOptions {
  portal?: boolean
}

interface AuthContextValue {
  status: AuthStatus
  user: User | null
  email: string | null
  signIn: (options?: SignInOptions) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue>({
  status: 'loading',
  user: null,
  email: null,
  signIn: async () => {},
  signOut: async () => {},
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  /*
   * Against a local seeded API this starts signed in rather than becoming so.
   *
   * `DEV_USER` is a module constant decided at build time — Vite replaces it with `null` in
   * any production build — so there is nothing to wait for and nothing to subscribe to. An
   * effect that set the status instead would render the "Signing you in…" screen first and
   * then replace it, which is both a visible flash and a cascading render the lint rule is
   * right to object to.
   */
  const [status, setStatus] = useState<AuthStatus>(DEV_USER ? 'signed-in' : 'loading')
  const queryClient = useQueryClient()

  useEffect(() => {
    // Finish any pending redirect sign-in before trusting onAuthStateChanged. Without this
    // the redirect path never completes and the user is silently returned to /login.
    void completeRedirectSignIn()
  }, [])

  useEffect(() => {
    /*
     * Nothing to subscribe to against a local seeded API: there is no Firebase project, and
     * the API is not checking a token anyway (see lib/devAuth). Subscribing would drive the
     * status to signed-out and bounce every route to the login screen.
     */
    if (DEV_USER) return
    return onAuthStateChanged(auth, (next) => {
      setUser(next)
      if (!next) {
        setStatus('signed-out')
      } else if (!isInternalEmail(next.email)) {
        // Client-side courtesy check so we can show a clear screen. The backend rejects
        // these too — the `hd` parameter on the Google provider is only a hint.
        setStatus('external')
      } else {
        setStatus('signed-in')
      }
    })
  }, [])

  useEffect(() => {
    // Custom claims can change (role reassignment). A fresh token may carry a different
    // ability, so drop the cached ruleset rather than trusting a stale one.
    return onIdTokenChanged(auth, () => {
      void queryClient.invalidateQueries({ queryKey: qk.abilitiesPrefix() })
    })
  }, [queryClient])

  const signIn = useCallback(async (options?: SignInOptions) => {
    const provider = options?.portal ? portalGoogleProvider : googleProvider
    try {
      await signInWithGoogle(provider)
    } catch (err) {
      const code = (err as { code?: string }).code ?? ''

      // The user closed the popup, or a second click superseded the first. Both are
      // deliberate actions — do nothing. Falling back to a redirect here (which this used
      // to do) sends them to Google and bounces them straight back to the login page.
      if (code.includes('popup-closed-by-user') || code.includes('cancelled-popup-request')) {
        return
      }

      // The browser actually refused to open a popup — embedded webviews, Safari ITP.
      // A redirect is the only way through, and completeRedirectSignIn() finishes it.
      if (code.includes('popup-blocked') || code.includes('operation-not-supported')) {
        await signInWithGoogleRedirect(provider)
        return
      }

      if (code.includes('unauthorized-domain')) {
        throw new Error(
          `${window.location.hostname} is not an authorized domain for this Firebase project. ` +
            'Add it under Authentication → Settings → Authorized domains.',
        )
      }

      throw err
    }
  }, [])

  const signOut = useCallback(async () => {
    await firebaseSignOut()
    queryClient.clear()
  }, [queryClient])

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, email: user?.email ?? null, signIn, signOut }),
    [status, user, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  return useContext(AuthContext)
}
